import * as React from 'react';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Trash2, Clock, AlertCircle } from 'lucide-react';
import { useSchool } from '@/hooks/use-schools';
import { validateTimeRange } from '@/lib/validation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { Schedule, Period, LunchWave, ScheduleModification, RegularPeriod, CustomPeriod, DayType, PeriodType } from '@/types/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Day of week options for core schedules (locked days)
const DAY_OF_WEEK_OPTIONS: { value: 'M' | 'T' | 'W' | 'TH' | 'F'; label: string }[] = [
  { value: 'M', label: 'Mon' },
  { value: 'T', label: 'Tue' },
  { value: 'W', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'F', label: 'Fri' },
];

const MODIFICATIONS: { value: ScheduleModification; label: string }[] = [
  { value: 'ED', label: 'Early Dismissal' },
  { value: 'EXAM', label: 'Exam' },
  { value: 'LS60', label: 'Late Start (1hr)' },
  { value: 'LS90', label: 'Late Start (1.5hr)' },
  { value: 'LS105', label: 'Late Start (1hr45)' },
  { value: 'LS120', label: 'Late Start (2hr)' },
  { value: 'LS150', label: 'Late Start (2.5hr)' },
  { value: 'LS180', label: 'Late Start (3hr)' },
  { value: 'HR', label: 'Homeroom' },
  { value: 'ADVISORY', label: 'Advisory' },
  { value: 'ASSEMBLY', label: 'Assembly' },
  { value: 'ALL-PERIODS-MEET', label: 'All Periods Meet' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'PEP', label: 'Pep Rally' },
  { value: 'OTHER', label: 'Other' },
];

// Helper to sanitize custom modification names
const sanitizeModification = (val?: string) =>
  val
    ?.toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]+/g, '');

const formSchema = z.object({
  variant: z.enum(['core', 'modified', 'special']),
  dayTypeId: z.string().optional(),
  dayOfWeek: z.enum(['M', 'T', 'W', 'TH', 'F']).nullable().optional(),
  modification: z.string().optional(),
  otherModification: z.string().optional().transform(sanitizeModification),
  date: z.date().optional(),
  displayName: z.string().min(1, 'Name is required'),
  isInLsc: z.boolean().optional(),
  isInAcf: z.boolean().optional(),
}).refine((data) => {
  // Day type is required for core and modified schedules
  if (data.variant === 'core' || data.variant === 'modified') {
    return !!data.dayTypeId;
  }
  return true;
}, {
  message: 'Day type is required',
  path: ['dayTypeId'],
}).refine((data) => {
  // Modification is required for modified schedules
  if (data.variant === 'modified') {
    return !!data.modification;
  }
  return true;
}, {
  message: 'Modification is required',
  path: ['modification'],
}).refine((data) => {
  // Date is required for special schedules
  if (data.variant === 'special') {
    return !!data.date;
  }
  return true;
}, {
  message: 'Date is required',
  path: ['date'],
});

interface SimpleScheduleFormProps {
  schoolId: string;
  existingSchedule?: Schedule;
  /** If true, treat existingSchedule as initial data for a NEW schedule (copy/parsed mode) */
  isNewFromExisting?: boolean;
  onSave: (schedule: Partial<Schedule>) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
}

export function SimpleScheduleForm({
  schoolId,
  existingSchedule,
  onSave,
  onCancel,
  onDelete,
}: SimpleScheduleFormProps) {
  const { data: school } = useSchool(schoolId);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [periods, setPeriods] = useState<Partial<Period>[]>(() => {
    // When loading existing schedule, detect custom periods
    if (existingSchedule?.periods) {
      return existingSchedule.periods.map(period => {
        // A period is custom if it doesn't have dayType and periodType
        const isCustom = !(period as any).dayType || !(period as any).periodType;
        return { ...period, isCustom } as any;
      });
    }
    return [];
  });
  const [lunchWaves, setLunchWaves] = useState<Partial<LunchWave>[]>(
    existingSchedule?.lunchWaves || []
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      variant: existingSchedule?.variant || 'core',
      dayTypeId: existingSchedule?.variant === 'core' || existingSchedule?.variant === 'modified' 
        ? existingSchedule.dayType?.id 
        : '',
      dayOfWeek: existingSchedule?.variant === 'core' ? (existingSchedule.dayOfWeek || null) : null,
      modification: existingSchedule?.variant === 'modified' ? existingSchedule.modification : '',
      otherModification: existingSchedule?.variant === 'modified' && existingSchedule.modification === 'OTHER'
        ? existingSchedule.otherModification || ''
        : '',
      date: existingSchedule?.variant === 'special' ? existingSchedule.date : undefined,
      displayName: existingSchedule?.displayName || '',
      isInLsc: existingSchedule?.variant === 'core' || existingSchedule?.variant === 'modified'
        ? existingSchedule.isInLsc 
        : true,
      isInAcf: existingSchedule?.variant === 'core' ? existingSchedule.isInAcf : true,
    },
  });

  const variant = form.watch('variant');
  const dayTypeId = form.watch('dayTypeId');
  const dayOfWeek = form.watch('dayOfWeek');
  const modification = form.watch('modification');
  const otherModification = form.watch('otherModification');
  const selectedDate = form.watch('date');

  // Helper to capitalize words
  const capitalizeWord = (str: string) => 
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  // Auto-generate display name
  React.useEffect(() => {
    const dayType = school?.dayTypes?.find((dt) => dt.id === dayTypeId);
    if (!dayType) return;

    if (variant === 'core') {
      const dayOfWeekLabel = DAY_OF_WEEK_OPTIONS.find(d => d.value === dayOfWeek)?.label;
      if (dayOfWeekLabel) {
        form.setValue('displayName', `${capitalizeWord(dayType.name)} - ${dayOfWeekLabel}`);
      } else {
        form.setValue('displayName', capitalizeWord(dayType.name));
      }
    } else if (variant === 'modified' && modification) {
      const modLabel = MODIFICATIONS.find((m) => m.value === modification)?.label;
      form.setValue('displayName', `${capitalizeWord(dayType.name)} - ${modLabel || modification}`);
    }
  }, [variant, dayTypeId, dayOfWeek, modification, school, form]);

  React.useEffect(() => {
    if (variant === 'special' && selectedDate) {
      form.setValue('displayName', `Special - ${format(selectedDate, 'MMM d')}`);
    }
  }, [variant, selectedDate, form]);

  // Period management with smart defaults
  const [periodDuration, setPeriodDuration] = useState(45);
  const [periodGap, setPeriodGap] = useState(5);

  const calculateDuration = (start: string, end: string): number => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
  };

  const addMinutesToTime = (time: string, minutes: number): string => {
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  const getNextPeriodTimes = (): { startTime: string; endTime: string } => {
    if (periods.length === 0) {
      return { startTime: '08:00', endTime: '08:45' };
    }
    const lastPeriod = periods[periods.length - 1];
    const startTime = addMinutesToTime(lastPeriod.endTime || '08:00', periodGap);
    const endTime = addMinutesToTime(startTime, periodDuration);
    return { startTime, endTime };
  };

  const addPeriod = () => {
    const { startTime, endTime } = getNextPeriodTimes();
    const dayType = school?.dayTypes?.find((dt) => dt.id === dayTypeId) || school?.dayTypes?.[0];
    
    // Check if we can configure period types (not just ACF)
    const canConfigurePeriodTypes = school?.periodTypes && school.periodTypes.length > 0;
    
    // Get the next period type based on how many periods already exist
    // If we've exceeded available period types, wrap around or use fallback
    const periodTypeIndex = periods.length % (school?.periodTypes?.length || 1);
    const periodType = school?.periodTypes?.[periodTypeIndex];
    
    const newPeriod: Partial<Period> = {
      id: `temp-${Date.now()}`,
      name: periodType?.name || `Period ${periods.length + 1}`,
      startTime,
      endTime,
      // Track whether this is a custom period
      isCustom: false,
    } as any;

    // Add period/day types if school has period types configured
    if (canConfigurePeriodTypes && dayType && periodType) {
      (newPeriod as Partial<RegularPeriod>).dayType = { id: dayType.id, name: dayType.name };
      (newPeriod as Partial<RegularPeriod>).periodType = { id: periodType.id, name: periodType.name };
    }

    setPeriods([...periods, newPeriod]);
  };

  const updatePeriod = (index: number, updates: Partial<Period>) => {
    setPeriods(prevPeriods => {
      const newPeriods = [...prevPeriods];
      newPeriods[index] = { ...newPeriods[index], ...updates };
      return newPeriods;
    });
  };

  const deletePeriod = (index: number) => {
    setPeriods(periods.filter((_, i) => i !== index));
  };

  const duplicatePeriod = (index: number) => {
    const period = periods[index];
    const { startTime, endTime } = getNextPeriodTimes();
    setPeriods([...periods, { ...period, id: `temp-${Date.now()}`, startTime, endTime }]);
  };

  // Check for overlapping periods
  const checkOverlap = (index: number): boolean => {
    const period = periods[index];
    if (!period || !period.startTime || !period.endTime) return false;
    
    const startMinutes = calculateDuration('00:00', period.startTime);
    const endMinutes = calculateDuration('00:00', period.endTime);

    return periods.some((p, i) => {
      if (i === index || !p.startTime || !p.endTime) return false;
      const pStart = calculateDuration('00:00', p.startTime);
      const pEnd = calculateDuration('00:00', p.endTime);
      return (startMinutes < pEnd && endMinutes > pStart);
    });
  };

  // Lunch wave management
  const addLunchWave = () => {
    if (!school?.lunchSettings || school.lunchSettings.type !== 'waves') return;
    
    const slots = school.lunchSettings.slots;
    if (lunchWaves.length >= slots.length) return;
    
    const usedSlots = new Set(lunchWaves.map(w => w.slot?.slot).filter((s): s is number => s !== undefined));
    const availableSlot = slots.find((s: { id: string; slot: number }) => !usedSlots.has(s.slot));
    
    if (!availableSlot) return;
    
    const name = school.lunchSettings.isAlphabetic 
      ? String.fromCharCode(65 + lunchWaves.length) // A, B, C...
      : String(lunchWaves.length + 1); // 1, 2, 3...
    
    const { startTime, endTime } = getNextPeriodTimes();
    
    setLunchWaves([...lunchWaves, {
      id: `temp-${Date.now()}`,
      name,
      slot: availableSlot,
      startTime,
      endTime,
    }]);
  };

  const updateLunchWave = (index: number, updates: Partial<LunchWave>) => {
    const newWaves = [...lunchWaves];
    newWaves[index] = { ...newWaves[index], ...updates };
    setLunchWaves(newWaves);
  };

  const deleteLunchWave = (index: number) => {
    setLunchWaves(lunchWaves.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: any) => {
    if (periods.length === 0) {
      toast.error('At least one period is required');
      return;
    }

    for (let i = 0; i < periods.length; i++) {
      const p = periods[i];
      if (!p.name?.trim()) {
        toast.error(`Period ${i + 1} is missing a name`);
        return;
      }
      if (!p.startTime || !p.endTime) {
        toast.error(`Period "${p.name}" is missing start or end time`);
        return;
      }
      const timeError = validateTimeRange(p.startTime, p.endTime);
      if (timeError) {
        toast.error(`Period "${p.name}": ${timeError}`);
        return;
      }
    }

    for (let i = 0; i < lunchWaves.length; i++) {
      const w = lunchWaves[i];
      if (!w.startTime || !w.endTime) {
        toast.error(`Lunch wave "${w.name || i + 1}" is missing start or end time`);
        return;
      }
      const timeError = validateTimeRange(w.startTime, w.endTime);
      if (timeError) {
        toast.error(`Lunch wave "${w.name || i + 1}": ${timeError}`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const dayType = school?.dayTypes?.find((dt) => dt.id === data.dayTypeId);
      
      // Sort periods by start time before saving
      const sortedPeriods = [...periods].sort((a, b) => {
        const aTime = calculateDuration('00:00', a.startTime || '00:00');
        const bTime = calculateDuration('00:00', b.startTime || '00:00');
        return aTime - bTime;
      });
      
      let scheduleData: Partial<Schedule> = {
        displayName: data.displayName,
        periods: sortedPeriods as Period[],
        lunchWaves: lunchWaves as LunchWave[],
        // Preserve existing schedule fields when editing
        ...(existingSchedule?.id && { id: existingSchedule.id }),
        ...(existingSchedule?.genId && { genId: existingSchedule.genId }),
        ...(existingSchedule?.schoolId && { schoolId: existingSchedule.schoolId }),
      };

      if (data.variant === 'core' && dayType) {
        scheduleData = {
          ...scheduleData,
          variant: 'core',
          dayType,
          dayOfWeek: data.dayOfWeek || undefined,
          isInLsc: data.isInLsc ?? true,
          isInAcf: data.isInAcf ?? true,
          // When ACF (grid) is enabled, order is required
          // Preserve existing order if available, otherwise default to 0
          ...(data.isInAcf && {
            order: (existingSchedule?.variant === 'core' && existingSchedule.order !== undefined)
              ? existingSchedule.order
              : 0
          }),
        };
      } else if (data.variant === 'modified' && dayType) {
        const isOther = data.modification === 'OTHER';
        scheduleData = {
          ...scheduleData,
          variant: 'modified',
          dayType,
          modification: data.modification,
          isInLsc: data.isInLsc ?? true,
          ...(isOther && data.otherModification && { otherModification: data.otherModification }),
        };
      } else if (data.variant === 'special') {
        scheduleData = {
          ...scheduleData,
          variant: 'special',
          date: data.date,
        };
      }

      await onSave(scheduleData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!school) return null;

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  const schoolPrimaryColor = school?.primaryColor || '#1e40af';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-700">
        {/* Schedule Configuration */}
        <Card className="border-2 transition-colors" style={{ borderColor: `${schoolPrimaryColor}20` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-semibold">
              Schedule Configuration
            </CardTitle>
            <CardDescription>
              Define the schedule type and basic information
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs value={variant} onValueChange={(v) => form.setValue('variant', v as any)} className="w-full">
              <TabsList 
                className="grid w-full grid-cols-3 h-11 mb-6 border-2 p-1"
                style={{ 
                  borderColor: `${schoolPrimaryColor}40`,
                  background: `${schoolPrimaryColor}08`
                }}
              >
                <TabsTrigger 
                  value="core" 
                  className="text-sm font-medium transition-all data-[state=active]:shadow-sm"
                  style={variant === 'core' ? { 
                    backgroundColor: schoolPrimaryColor,
                    color: getContrastColor(schoolPrimaryColor)
                  } : {}}
                >
                  📅 Core
                </TabsTrigger>
                <TabsTrigger 
                  value="modified" 
                  className="text-sm font-medium transition-all data-[state=active]:shadow-sm"
                  style={variant === 'modified' ? { 
                    backgroundColor: schoolPrimaryColor,
                    color: getContrastColor(schoolPrimaryColor)
                  } : {}}
                >
                  ⚙️ Modified
                </TabsTrigger>
                <TabsTrigger 
                  value="special" 
                  className="text-sm font-medium transition-all data-[state=active]:shadow-sm"
                  style={variant === 'special' ? { 
                    backgroundColor: schoolPrimaryColor,
                    color: getContrastColor(schoolPrimaryColor)
                  } : {}}
                >
                  ⭐ Special
                </TabsTrigger>
              </TabsList>

              {/* Core Schedule */}
              <TabsContent value="core" className="mt-0 space-y-5">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Display Name</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="e.g., A Day Schedule" 
                            className="h-11 text-base font-medium transition-all hover:border-primary/60 focus:ring-2 focus:ring-primary/20" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 grid-cols-2">
                    <FormField
                      control={form.control}
                      name="dayTypeId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">Day Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-11 text-base transition-all hover:border-primary/60">
                                <SelectValue placeholder="Select day type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {school.dayTypes.map((dt) => (
                                <SelectItem key={dt.id} value={dt.id} className="text-base">
                                  {dt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dayOfWeek"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">Day of Week (Optional)</FormLabel>
                          <Select 
                            onValueChange={(value) => field.onChange(value === 'none' ? null : value)} 
                            value={field.value || 'none'}
                          >
                            <FormControl>
                              <SelectTrigger className="h-11 text-base transition-all hover:border-primary/60">
                                <SelectValue placeholder="Lock to specific day" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none" className="text-base text-muted-foreground">
                                No lock (follows rotation)
                              </SelectItem>
                              {DAY_OF_WEEK_OPTIONS.map((day) => (
                                <SelectItem key={day.value} value={day.value} className="text-base">
                                  {day.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription className="text-xs">
                            Lock this schedule to a specific weekday
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />
                
                <div className="flex items-center justify-center gap-12">
                  <FormField
                    control={form.control}
                    name="isInLsc"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 space-y-0">
                        <FormLabel className="text-base font-medium cursor-pointer order-1">
                          Local Schedule Change
                        </FormLabel>
                        <FormControl className="order-2">
                          <div 
                            onClick={() => field.onChange(!field.value)}
                            className={cn(
                              "relative inline-flex h-7 w-12 cursor-pointer items-center rounded-full transition-colors",
                              field.value ? "bg-primary" : "bg-muted"
                            )}
                          >
                            <span
                              className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform",
                                field.value ? "translate-x-6" : "translate-x-1"
                              )}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="isInAcf"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 space-y-0">
                        <FormLabel className="text-base font-medium cursor-pointer order-1">
                          Add Class Flow
                        </FormLabel>
                        <FormControl className="order-2">
                          <div 
                            onClick={() => field.onChange(!field.value)}
                            className={cn(
                              "relative inline-flex h-7 w-12 cursor-pointer items-center rounded-full transition-colors",
                              field.value ? "bg-primary" : "bg-muted"
                            )}
                          >
                            <span
                              className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform",
                                field.value ? "translate-x-6" : "translate-x-1"
                              )}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              {/* Modified Schedule */}
              <TabsContent value="modified" className="mt-0 space-y-5">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Display Name</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="e.g., A Day - Early Dismissal" 
                            className="h-11 text-base font-medium transition-all hover:border-primary/60 focus:ring-2 focus:ring-primary/20" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 grid-cols-2">
                    <FormField
                      control={form.control}
                      name="dayTypeId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">Day Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-11 text-base transition-all hover:border-primary/60">
                                <SelectValue placeholder="Select day type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {school.dayTypes.map((dt) => (
                                <SelectItem key={dt.id} value={dt.id} className="text-base">
                                  {dt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="modification"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">Modification</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-11 text-base transition-all hover:border-primary/60">
                                <SelectValue placeholder="Select modification" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {MODIFICATIONS.map((m) => (
                                <SelectItem key={m.value} value={m.value} className="text-base">
                                  {m.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Custom modification name input for "OTHER" */}
                  {modification === 'OTHER' && (
                    <FormField
                      control={form.control}
                      name="otherModification"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">Custom Modification Name</FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              placeholder="e.g., RALLY, PEP-RALLY, FIELD-DAY" 
                              className="h-11 text-base font-medium uppercase transition-all hover:border-primary/60 focus:ring-2 focus:ring-primary/20"
                              onChange={(e) => {
                                const sanitized = sanitizeModification(e.target.value) || '';
                                field.onChange(sanitized);
                              }}
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Enter a custom modification type (letters, numbers, and hyphens only)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <Separator />

                <div className="flex items-center justify-center">
                  <FormField
                    control={form.control}
                    name="isInLsc"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 space-y-0">
                        <FormLabel className="text-base font-medium cursor-pointer order-1">
                          Local Schedule Change
                        </FormLabel>
                        <FormControl className="order-2">
                          <div 
                            onClick={() => field.onChange(!field.value)}
                            className={cn(
                              "relative inline-flex h-7 w-12 cursor-pointer items-center rounded-full transition-colors",
                              field.value ? "bg-primary" : "bg-muted"
                            )}
                          >
                            <span
                              className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform",
                                field.value ? "translate-x-6" : "translate-x-1"
                              )}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              {/* Special Schedule */}
              <TabsContent value="special" className="mt-0 space-y-5">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Display Name</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="e.g., Special - Holiday" 
                            className="h-11 text-base font-medium transition-all hover:border-primary/60 focus:ring-2 focus:ring-primary/20" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm font-medium mb-2">Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'h-11 pl-3 text-left font-normal justify-start text-base w-full',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => {
                                // Compare dates only (not times) to allow selecting today
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const compareDate = new Date(date);
                                compareDate.setHours(0, 0, 0, 0);
                                // Disable past dates
                                if (compareDate < today) return true;
                                // Disable weekends (0 = Sunday, 6 = Saturday)
                                const dayOfWeek = date.getDay();
                                return dayOfWeek === 0 || dayOfWeek === 6;
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Periods Configuration */}
        <Card className="border-2 transition-colors" style={{ borderColor: `${schoolPrimaryColor}20` }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Periods</CardTitle>
            <CardDescription className="mt-1">
              Configure bell times and period structure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Period Controls - Helper inputs (not part of form validation) */}
            <div className="bg-muted/30 rounded-lg p-4 border">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Add Controls</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="period-duration" className="text-xs mb-1.5 font-medium">Duration (min)</Label>
                  <input
                    id="period-duration"
                    type="number"
                    min={1}
                    step={5}
                    value={periodDuration}
                    onChange={(e) => setPeriodDuration(Number(e.target.value))}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-all duration-150 ease-out file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary focus-visible:shadow-md hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
                    form="non-existent-form"
                  />
                </div>
                <div>
                  <Label htmlFor="period-gap" className="text-xs mb-1.5 font-medium">Gap (min)</Label>
                  <input
                    id="period-gap"
                    type="number"
                    min={0}
                    step={5}
                    value={periodGap}
                    onChange={(e) => setPeriodGap(Number(e.target.value))}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-all duration-150 ease-out file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary focus-visible:shadow-md hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
                    form="non-existent-form"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    type="button" 
                    onClick={addPeriod} 
                    className="group w-full h-9 transition-all duration-200 ease-out hover:shadow-md"
                    style={{ 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:rotate-90" />
                    Add Period
                  </Button>
                </div>
              </div>
            </div>

            {periods.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground border-2 border-dashed rounded-lg transition-all duration-300 hover:border-primary/30 hover:bg-muted/10">
                <div className="rounded-full p-4 bg-muted/50 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Clock className="h-8 w-8" style={{ color: schoolPrimaryColor }} />
                </div>
                <p className="font-semibold text-base">No periods yet</p>
                <p className="text-sm mt-2 text-muted-foreground/80">Click "Add Period" above to create your first period</p>
              </div>
            ) : (
              <div className="space-y-2">
                {periods
                  .map((period, index) => ({ period, index, originalIndex: index }))
                  .sort((a, b) => {
                    const aTime = calculateDuration('00:00', a.period.startTime || '00:00');
                    const bTime = calculateDuration('00:00', b.period.startTime || '00:00');
                    return aTime - bTime;
                  })
                  .map(({ period, originalIndex }, displayIndex) => {
                    const duration = calculateDuration(period.startTime || '00:00', period.endTime || '00:00');
                    const hasOverlap = checkOverlap(originalIndex);
                    const isInvalid = duration < 0;
                    // Show regular/custom toggle if school has period types configured
                    const canConfigurePeriodTypes = school?.periodTypes && school.periodTypes.length > 0;
                    
                    return (
                      <div
                        key={period.id || originalIndex}
                        className={cn(
                          "group flex items-center gap-3 py-3 px-4 rounded-lg border-2 transition-all duration-200 ease-out bg-card hover:shadow-lg hover:-translate-y-0.5",
                          hasOverlap && "border-destructive bg-destructive/5 hover:bg-destructive/10",
                          isInvalid && "border-orange-500 bg-orange-50 dark:bg-orange-950/20 hover:bg-orange-100 dark:hover:bg-orange-950/30",
                          !hasOverlap && !isInvalid && "hover:border-primary/60"
                        )}
                        style={!hasOverlap && !isInvalid ? { borderColor: `${schoolPrimaryColor}20` } : {}}
                      >
                        {canConfigurePeriodTypes ? (
                          <>
                            {(period as any).isCustom ? (
                              // Custom period - just name input
                              <>
                                <Select
                                  value="custom"
                                  onValueChange={(value) => {
                                    if (value !== 'custom') {
                                      // Switching to regular - set defaults
                                      const dayType = school.dayTypes?.find(dt => dt.id === value);
                                      const periodType = school.periodTypes?.[0];
                                      if (dayType && periodType) {
                                        updatePeriod(originalIndex, {
                                          isCustom: false,
                                          name: periodType.name,
                                          dayType: { id: dayType.id, name: dayType.name },
                                          periodType: { id: periodType.id, name: periodType.name },
                                        } as any);
                                      }
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-9 w-[120px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="custom">Custom</SelectItem>
                                    {school.dayTypes.map((dt) => (
                                      <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input
                                  placeholder="Period name"
                                  value={period.name || ''}
                                  onChange={(e) => updatePeriod(originalIndex, { name: e.target.value })}
                                  className="h-9 w-[180px] font-medium transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                                  autoFocus={!period.name}
                                />
                              </>
                            ) : (
                              // Regular period - day type and period type
                              <>
                                <Select
                                  value={(period as any).dayType?.id || ''}
                                  onValueChange={(value) => {
                                    if (value === 'custom') {
                                      // Switching to custom - preserve the period name
                                      const currentName = (period as any).periodType?.name || period.name || `Period ${displayIndex + 1}`;
                                      updatePeriod(originalIndex, {
                                        isCustom: true,
                                        name: currentName,
                                        dayType: undefined,
                                        periodType: undefined,
                                      } as any);
                                    } else {
                                      const dayType = school.dayTypes?.find(dt => dt.id === value);
                                      if (dayType) {
                                        updatePeriod(originalIndex, {
                                          dayType: { id: dayType.id, name: dayType.name },
                                        } as any);
                                      }
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-9 w-[120px]">
                                    <SelectValue placeholder="Day type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="custom">Custom</SelectItem>
                                    {school.dayTypes.map((dt) => (
                                      <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                
                                <Select
                                  value={(period as any).periodType?.id || ''}
                                  onValueChange={(value) => {
                                    const periodType = school.periodTypes?.find(pt => pt.id === value);
                                    if (periodType) {
                                      updatePeriod(originalIndex, {
                                        name: periodType.name,
                                        periodType: { id: periodType.id, name: periodType.name },
                                      } as any);
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-9 w-[120px]">
                                    <SelectValue placeholder="Period type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {school.periodTypes.map((pt) => (
                                      <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </>
                            )}
                          </>
                        ) : (
                          <Input
                            placeholder="Period name"
                            value={period.name || ''}
                            onChange={(e) => updatePeriod(originalIndex, { name: e.target.value })}
                            className="h-9 w-[180px] font-medium transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                        
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={period.startTime || ''}
                            onChange={(e) => updatePeriod(originalIndex, { startTime: e.target.value })}
                            className="h-9 w-[120px] font-mono [&::-webkit-calendar-picker-indicator]:hidden transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                          />
                          <span className="text-sm text-muted-foreground font-bold">→</span>
                          <Input
                            type="time"
                            value={period.endTime || ''}
                            onChange={(e) => updatePeriod(originalIndex, { endTime: e.target.value })}
                            className="h-9 w-[120px] font-mono [&::-webkit-calendar-picker-indicator]:hidden transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        
                        <Badge 
                          variant="secondary" 
                          className="font-mono text-sm h-7 px-3 shrink-0"
                          style={{ 
                            backgroundColor: `${schoolPrimaryColor}15`,
                            color: schoolPrimaryColor,
                            borderColor: `${schoolPrimaryColor}30`,
                            borderWidth: '1px'
                          }}
                        >
                          {duration}m
                        </Badge>
                        
                        {hasOverlap && (
                          <Badge variant="destructive" className="text-xs h-7 animate-in fade-in-0 zoom-in-95 duration-200">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Overlap
                          </Badge>
                        )}
                        {isInvalid && (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-600 h-7 animate-in fade-in-0 zoom-in-95 duration-200">
                            Invalid
                          </Badge>
                        )}
                        
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => deletePeriod(originalIndex)}
                          className="h-9 w-9 ml-auto transition-all duration-200 ease-out opacity-0 group-hover:opacity-100 hover:scale-110 hover:text-destructive hover:bg-destructive/15 active:scale-95"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lunch Waves (if applicable) */}
        {school?.lunchSettings?.type === 'waves' && (
          <Card className="border-2 transition-colors" style={{ borderColor: `${schoolPrimaryColor}20` }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">Lunch Waves</CardTitle>
              <CardDescription className="mt-1">
                Configure lunch wave times (optional)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {lunchWaves.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground border-2 border-dashed rounded-lg transition-all duration-300 hover:border-primary/30 hover:bg-muted/10">
                  <div className="text-5xl mb-4">🍔</div>
                  <p className="font-semibold text-base">No lunch waves yet</p>
                  <p className="text-sm mt-2 text-muted-foreground/80">Click "Add Lunch Wave" below to create your first wave</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {lunchWaves.map((wave, index) => {
                    const duration = calculateDuration(wave.startTime || '00:00', wave.endTime || '00:00');
                    return (
                      <div 
                        key={wave.id || index} 
                        className="group flex items-center gap-3 py-3 px-4 rounded-lg border-2 bg-card transition-all duration-200 ease-out hover:shadow-lg hover:-translate-y-0.5"
                        style={{ borderColor: `${schoolPrimaryColor}20` }}
                      >
                        <div className="text-2xl shrink-0">🍔</div>
                        <Input
                          placeholder="Name"
                          value={wave.name}
                          onChange={(e) => updateLunchWave(index, { name: e.target.value })}
                          className="w-24 h-9 font-medium transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                        />
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={wave.startTime}
                            onChange={(e) => updateLunchWave(index, { startTime: e.target.value })}
                            className="w-[120px] h-9 font-mono [&::-webkit-calendar-picker-indicator]:hidden transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                          />
                          <span className="text-sm text-muted-foreground font-bold">→</span>
                          <Input
                            type="time"
                            value={wave.endTime}
                            onChange={(e) => updateLunchWave(index, { endTime: e.target.value })}
                            className="w-[120px] h-9 font-mono [&::-webkit-calendar-picker-indicator]:hidden transition-all duration-150 ease-out hover:border-primary/60 focus:border-primary focus:shadow-md focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        <Badge 
                          variant="secondary" 
                          className="font-mono text-sm h-7 px-3 shrink-0"
                          style={{ 
                            backgroundColor: `${schoolPrimaryColor}15`,
                            color: schoolPrimaryColor,
                            borderColor: `${schoolPrimaryColor}30`,
                            borderWidth: '1px'
                          }}
                        >
                          {duration}m
                        </Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteLunchWave(index)}
                          className="h-9 w-9 ml-auto transition-all duration-200 ease-out opacity-0 group-hover:opacity-100 hover:scale-110 hover:text-destructive hover:bg-destructive/15 active:scale-95"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {lunchWaves.length < school.lunchSettings.slots.length && (
                <Button 
                  type="button" 
                  onClick={addLunchWave} 
                  className="group w-full transition-all duration-200 ease-out hover:shadow-md"
                  style={{ 
                    backgroundColor: `${schoolPrimaryColor}15`,
                    color: schoolPrimaryColor,
                    borderColor: `${schoolPrimaryColor}40`,
                    borderWidth: '2px'
                  }}
                >
                  <Plus className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:rotate-90" />
                  Add Lunch Wave
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between gap-3 pt-6">
          <div>
            {existingSchedule?.id && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isSaving || isDeleting}
                className="transition-all hover:shadow-md"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Schedule
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel} 
              disabled={isSaving || isDeleting}
              className="min-w-[100px] transition-all hover:shadow-md"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving || isDeleting}
              className="min-w-[140px] transition-all hover:shadow-lg"
              style={{ 
                backgroundColor: schoolPrimaryColor,
                color: getContrastColor(schoolPrimaryColor)
              }}
            >
              {isSaving ? 'Saving...' : existingSchedule?.id ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{existingSchedule?.displayName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}

